// One square card on the phones heatmap board — a dark dashboard tile in
// three zones: header (name + status) / centre (big value) / footer
// (signal + battery). Colour encodes state; there is no accent bar.
// Every metric — fonts, paddings, widget sizes — scales off `size` so a
// card reads the same whether the board fits 30 phones or 150.

import type { KeyboardEvent } from 'react'
import type { PhoneCell, CardColors } from '../../lib/heatmapCells'
import { formatDuration, troubleToken } from '../../lib/heatmapCells'
import { BatteryGauge } from '../BatteryGauge'
import { SignalBars } from './SignalBars'
import { TroubleIcon } from './TroubleIcon'

interface PhoneCardProps {
  cell: PhoneCell
  /** Side length in px, from computeLayout. */
  size: number
  colors: CardColors
  /** When set, the card becomes a button — click / Enter / Space fire it.
   * Omitted → the card stays a plain, non-interactive tile. */
  onSelect?: () => void
}

/** Below this the card is too small for the footer — name + value only. */
const FOOTER_MIN = 92

export function PhoneCard({ cell, size, colors, onSelect }: PhoneCardProps) {
  const pad = Math.round(size * 0.075)
  const nameFont = Math.max(9, Math.min(15, size * 0.092))
  const showFooter = cell.state !== 'idle' && size >= FOOTER_MIN
  const footH = Math.max(9, Math.min(18, size * 0.115))

  const title =
    cell.state === 'trouble' && cell.reasons.length > 0
      ? `${cell.name} · ${cell.sim}\n${cell.reasons.map((r) => r.label).join('\n')}`
      : `${cell.name} · ${cell.sim}`

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Space/Enter activate a role=button — mirror native button behaviour.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect?.()
    }
  }

  return (
    <div
      data-testid="phone-card"
      data-state={cell.state}
      title={title}
      onClick={onSelect}
      onKeyDown={onSelect ? handleKeyDown : undefined}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      className={onSelect ? 'transition hover:brightness-110' : undefined}
      style={{
        width: size,
        height: size,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: Math.round(size * 0.07),
        padding: pad,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
        cursor: onSelect ? 'pointer' : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        <span
          style={{
            color: cell.state === 'idle' ? '#6b7587' : '#e5e7eb',
            fontWeight: 600,
            fontSize: nameFont,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {cell.name}
        </span>
        <HeaderStatus cell={cell} size={size} accent={colors.accent} />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
        }}
      >
        <CardCenter cell={cell} size={size} accent={colors.accent} />
      </div>

      {showFooter && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <SignalBars level={cell.signal_bars} px={footH} />
          <BatteryGauge pct={cell.battery_pct} px={footH} />
        </div>
      )}
    </div>
  )
}

/** Top-right slot: active → pulsing "on air" dot (or ⚠ when stale);
 * trouble → severity number; idle → nothing. */
function HeaderStatus({
  cell,
  size,
  accent,
}: {
  cell: PhoneCell
  size: number
  accent: string
}) {
  if (cell.state === 'active') {
    if (cell.stale) {
      return (
        <span style={{ color: '#f59e0b', fontSize: size * 0.13, lineHeight: 1 }}>
          ⚠
        </span>
      )
    }
    const d = Math.max(5, Math.min(9, size * 0.058))
    return (
      <span
        className="animate-pulse"
        style={{
          width: d,
          height: d,
          borderRadius: '50%',
          background: '#34d399',
          display: 'block',
          flexShrink: 0,
        }}
      />
    )
  }
  if (cell.state === 'trouble') {
    return (
      <span
        style={{
          color: accent,
          fontWeight: 700,
          fontSize: Math.max(9, Math.min(15, size * 0.1)),
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {cell.trouble_score}
      </span>
    )
  }
  return null
}

/** Card centre: active → call timer; trouble → icon + token; idle → dot. */
function CardCenter({
  cell,
  size,
  accent,
}: {
  cell: PhoneCell
  size: number
  accent: string
}) {
  if (cell.state === 'active') {
    return (
      <span
        style={{
          color: accent,
          fontWeight: 700,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          fontSize: Math.max(13, Math.min(44, size * 0.21)),
        }}
      >
        {formatDuration(cell.in_call_seconds ?? 0)}
      </span>
    )
  }
  if (cell.state === 'trouble') {
    const reason = cell.reasons[0]
    if (!reason) return null
    const glyphPx = Math.max(20, Math.min(54, size * 0.3))
    let glyph
    if (reason.type === 'weak_signal') {
      glyph = <SignalBars level={1} px={glyphPx} />
    } else if (reason.type === 'low_battery') {
      glyph = <BatteryGauge pct={cell.battery_pct} px={glyphPx * 0.6} withText={false} />
    } else {
      glyph = <TroubleIcon type={reason.type} size={glyphPx} />
    }
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: Math.round(size * 0.045),
          color: accent,
        }}
      >
        {glyph}
        <span
          style={{
            fontSize: Math.max(8, Math.min(16, size * 0.108)),
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        >
          {troubleToken(reason)}
        </span>
      </div>
    )
  }
  // idle — a quiet dot
  const d = Math.max(5, Math.min(10, size * 0.07))
  return (
    <span
      style={{ width: d, height: d, borderRadius: '50%', background: '#3d4658' }}
    />
  )
}
